<template>
  <md-autocomplete
    :disabled="isDisabled"
    :value="displayValue"
    :md-options="options"
    :required="required"
    @md-selected="opt => selectOption(opt)"
    @md-changed="term => changeSearchTerm(term)"
    @md-closed="onCloseOptionsPopup"
    @md-opened="handleOnOpen"
    ref="autoCompleteEle"
  >
    <label>{{ !isSwitchLocale ? label : $t(label) }}</label>
    <template slot="md-autocomplete-item" slot-scope="{ item, term }">
      <md-highlight-text :md-term="term"
        >{{ renderItem(item) }}
      </md-highlight-text>
    </template>
    <template slot="md-autocomplete-empty">
      <div
        v-if="!!refetchOptions && isFetching"
        class="absolute top-1/4 left-2/4"
      >
        <md-progress-spinner
          :md-diameter="30"
          :md-stroke="3"
          md-mode="indeterminate"
        ></md-progress-spinner>
      </div>
      <span v-else>{{ $t("common.no_results") }}</span>
    </template>
    <slot></slot>
  </md-autocomplete>
</template>

<script>
import queryHelper from "@/helpers/query-helper";
import debounce from "lodash/debounce";

export default {
  name: "isis-autocomplete",
  props: {
    options: {
      type: Array,
      default: () => []
    },
    refetchOptions: {
      type: Function,
      default: null
    },
    fields: {
      type: Object,
      default: () => ({})
    },
    displayValue: {
      // this value is search text
      type: [String, Number],
      default: ""
    },
    value: {
      // this value is real selection
      type: [String, Number, Object],
      default: ""
    },
    label: String,
    searchKey: String,
    required: {
      type: Boolean,
      default: false
    },
    fetchOnOpen: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    delayTime: {
      type: Number,
      default: 300
    },
    isSwitchLocale: {
      type: Boolean,
      default: true
    }
  },
  model: {
    prop: "display-value",
    event: "selected"
  },
  data() {
    return {
      isFetching: false
    };
  },
  mounted() {
    this.$refs.autoCompleteEle.$el
      .querySelector("input")
      .addEventListener("keydown", event => {
        if (event.keyCode === 13) {
          event.preventDefault();
          return false;
        }
      });
  },
  computed: {
    debounceSearchKey() {
      return debounce(function(term) {
        this.$emit("update:options", []);
        this.isFetching = true;
        this.getNewOptions()(term);
      }, this.delayTime);
    }
  },
  methods: {
    selectOption(opt) {
      const valField = this.fields.value;
      const textField = this.fields.text;
      const value = valField && opt ? opt[valField] : opt;
      const displayValue = textField && opt ? opt[textField] : opt;

      this.updateSelected(value, displayValue);
    },
    getNewOptions() {
      const self = this;
      const fetchOptions = function(term) {
        const queryObj = self.searchKey
          ? queryHelper.buildQuery([
              {
                name: self.searchKey,
                value: term
              }
            ])
          : null;

        self.refetchOptions(queryObj).finally(() => {
          self.isFetching = false;
        });
      };

      return fetchOptions;
    },

    changeSearchTerm(term) {
      const isTermChangeAfterSelected = term && term !== this.displayValue;
      const isClearQuery = !term && !!this.value;

      this.$emit("selected", term);

      // for options: reset options if need fetch from server
      if ((isTermChangeAfterSelected && this.refetchOptions) || isClearQuery) {
        this.debounceSearchKey(term);
      }

      // for selected
      if (isTermChangeAfterSelected || isClearQuery) {
        this.$emit("update:value", null);
      }
    },

    updateSelected(value, displayValue) {
      this.$emit("update:value", value);
      this.$emit("selected", displayValue);
    },

    // Feel free to use it combine with v-html to customize
    renderItem(item) {
      const txtField = this.fields.text;
      const text = txtField && item ? item[txtField] : item;

      return text;
    },

    onCloseOptionsPopup() {
      // ? Using setTimeout bcz md-closed called before md-selected
      setTimeout(() => {
        if (this.displayValue && !this.value) {
          this.updateSelected(null, null);
          // re-fetch list options if need fetch from server
          !!this.refetchOptions && this.getNewOptions()("");
        }
      }, 500);
    },
    handleOnOpen() {
      this.initOpenAutocompleteEvent();
      if (this.fetchOnOpen) {
        this.isFetching = true;
        this.getNewOptions()(this.displayValue);
      }
    },

    initOpenAutocompleteEvent() {
      const event = new CustomEvent("open-autocomplete", {
        detail: {
          label: this.label
        }
      });

      document.dispatchEvent(event);

      document.addEventListener(
        "open-autocomplete",
        this.handleOpenAutoCompleteListener
      );
    },

    destroyHandleOpenAutoCompleteListener() {
      document.removeEventListener(
        "open-autocomplete",
        this.handleOpenAutoCompleteListener
      );
    },

    handleOpenAutoCompleteListener(event) {
      const { detail } = event;
      if (detail.label === this.label) return;

      // ? when having no showed autoCompleteEle but component was mounted
      if (!this.$refs.autoCompleteEle) return;

      this.$refs.autoCompleteEle.hideOptions();
      this.$refs.autoCompleteEle.$nextTick(() => {
        this.hideOptionsMenu();
        this.destroyHandleOpenAutoCompleteListener();
      });
    },

    /**
     * hideOptions of md-autocomplete isn't working properly,
     * so we need handle for it
     */
    hideOptionsMenu() {
      this.$refs.autoCompleteEle.showMenu = false;
    }
  }
};
</script>

<style lang="scss" scoped></style>
